/**
 * NarrationDownloadDialog — Offline Narration Stage 4
 *
 * Platform-aware guide for setting up offline audio caching.
 *   • Detects iOS / Android / Desktop / PWA mode.
 *   • Shows step-by-step instructions per platform.
 *   • Shows cache status: which chapters are cached + estimated MB.
 *   • Provides "Clear All Cache" action.
 *   • Provides "Enable Offline Mode" toggle (stored in localStorage).
 *
 * Opened from the Downloads tab of NarrationStudioDialog.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Smartphone, Monitor, CheckCircle2, XCircle, Loader2,
  Download, Trash2, Wifi, WifiOff, Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// ── Platform detection ────────────────────────────────────────────────────────

function detectPlatform() {
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  const isPWA = window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
  return { isIOS, isAndroid, isMobile: isIOS || isAndroid, isPWA };
}

function getPlatformLabel({ isIOS, isAndroid, isPWA }) {
  if (isPWA) return 'PWA (Installed App)';
  if (isIOS) return 'iOS · Safari';
  if (isAndroid) return 'Android · Chrome';
  return 'Desktop Browser';
}

// ── Service Worker cache probe ────────────────────────────────────────────────

async function getAudioCacheStats() {
  if (!('caches' in window)) return null;
  try {
    const cacheNames = await caches.keys();
    const audioCaches = cacheNames.filter(n => n.includes('audio') || n.includes('narr'));
    if (!audioCaches.length) return { count: 0, sizeMb: 0, chapters: [] };

    let totalBytes = 0;
    const chapters = [];
    for (const name of audioCaches) {
      const cache = await caches.open(name);
      const keys = await cache.keys();
      for (const req of keys) {
        const resp = await cache.match(req);
        if (resp) {
          const blob = await resp.blob();
          totalBytes += blob.size;
          const url = req.url;
          const match = url.match(/chapter[_-]?(\d+)/i);
          if (match) chapters.push({ number: parseInt(match[1], 10), url, sizeMb: (blob.size / 1024 / 1024).toFixed(2) });
        }
      }
    }
    chapters.sort((a, b) => a.number - b.number);
    return { count: chapters.length, sizeMb: (totalBytes / 1024 / 1024).toFixed(1), chapters };
  } catch {
    return null;
  }
}

async function clearAudioCache() {
  if (!('caches' in window)) return 0;
  const cacheNames = await caches.keys();
  const audioCaches = cacheNames.filter(n => n.includes('audio') || n.includes('narr'));
  let cleared = 0;
  for (const name of audioCaches) {
    await caches.delete(name);
    cleared++;
  }
  return cleared;
}

// ── Platform instructions ─────────────────────────────────────────────────────

const PLATFORM_STEPS = {
  ios: {
    icon: Smartphone,
    title: 'iOS Offline Setup',
    steps: [
      'Open this app in Safari on your iPhone or iPad.',
      'Tap the Share button (rectangle with arrow) at the bottom of Safari.',
      'Tap "Add to Home Screen" and confirm.',
      'Reopen the app from your Home Screen — it will run as a full-screen PWA.',
      'Play a chapter while online to cache it. The audio will persist offline.',
    ],
    note: 'iOS Safari only allows offline caching when the app is installed to your Home Screen. Chapters you play once will be available offline automatically.',
    warning: 'Standard Safari tabs do not support persistent audio caching — the Home Screen install step is required.',
  },
  android: {
    icon: Smartphone,
    title: 'Android Offline Setup',
    steps: [
      'Open this app in Chrome on your Android device.',
      'Tap the three-dot menu in Chrome.',
      'Tap "Add to Home screen" or "Install app".',
      'Reopen from your home screen for the full PWA experience.',
      'Play any chapter while connected — it will be cached for offline use.',
    ],
    note: 'Chrome on Android supports full Service Worker caching. Chapters automatically cache on first play.',
    warning: null,
  },
  desktop: {
    icon: Monitor,
    title: 'Desktop Offline Setup',
    steps: [
      'Use Chrome or Edge for best offline support.',
      'Click the install icon in the address bar to install as a PWA (optional).',
      'Play chapters while online — they will be cached by the Service Worker.',
      'Return to your cached chapters anytime offline.',
    ],
    note: 'Desktop browsers support full Service Worker caching. No install required.',
    warning: null,
  },
  pwa: {
    icon: Monitor,
    title: 'PWA — Fully Configured',
    steps: [
      'You are already running as an installed PWA.',
      'Chapters are cached automatically on first play.',
      'Offline playback is fully supported in this mode.',
    ],
    note: 'Great — you are in the optimal setup for offline narration.',
    warning: null,
  },
};

function getPlatformKey({ isIOS, isAndroid, isPWA }) {
  if (isPWA) return 'pwa';
  if (isIOS) return 'ios';
  if (isAndroid) return 'android';
  return 'desktop';
}

// ── Readiness badge ───────────────────────────────────────────────────────────

function ReadinessBadge({ isPWA, isIOS, swSupported }) {
  const ready = isPWA || (!isIOS && swSupported);
  return (
    <div className={cn(
      'flex items-center gap-2 rounded-lg px-3 py-2 text-[13px] font-medium',
      ready ? 'bg-emerald-500/10 text-emerald-700' : 'bg-amber-500/10 text-amber-700',
    )}>
      {ready
        ? <><CheckCircle2 className="h-4 w-4" /> Offline caching is active</>
        : <><Info className="h-4 w-4" /> Follow the setup steps below to enable offline playback</>}
    </div>
  );
}

// ── Main dialog ───────────────────────────────────────────────────────────────

export function NarrationDownloadDialog({ open, onOpenChange }) {
  const [platform, setPlatform] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);
  const [cacheBusy, setCacheBusy] = useState(false);
  const [offlineEnabled, setOfflineEnabled] = useState(() => {
    try { return localStorage.getItem('pw_narr_offline') !== '0'; } catch { return true; }
  });
  const [swSupported, setSwSupported] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPlatform(detectPlatform());
    setSwSupported('serviceWorker' in navigator && 'caches' in window);
    setCacheBusy(true);
    getAudioCacheStats().then(stats => {
      setCacheStats(stats);
      setCacheBusy(false);
    });
  }, [open]);

  const handleClearCache = useCallback(async () => {
    setCacheBusy(true);
    try {
      const n = await clearAudioCache();
      toast.success(n > 0 ? `Cleared ${n} audio cache${n !== 1 ? 's' : ''}` : 'No audio caches found');
      setCacheStats({ count: 0, sizeMb: '0.0', chapters: [] });
    } catch {
      toast.error('Could not clear cache');
    } finally {
      setCacheBusy(false);
    }
  }, []);

  const toggleOffline = (checked) => {
    setOfflineEnabled(checked);
    try { localStorage.setItem('pw_narr_offline', checked ? '1' : '0'); } catch { /* noop */ }
    toast.info(checked ? 'Offline mode enabled — chapters will be cached on play.' : 'Offline mode disabled — chapters will not be cached.');
  };

  const platformKey = platform ? getPlatformKey(platform) : 'desktop';
  const guide = PLATFORM_STEPS[platformKey] || PLATFORM_STEPS.desktop;
  const GuideIcon = guide.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="narration-download-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WifiOff className="h-4 w-4 text-primary" />
            Offline Narration Setup
          </DialogTitle>
          <DialogDescription>
            Configure offline audio caching for your platform.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto max-h-[70vh] scroll-thin pr-1">

          {/* Platform badge */}
          <div className="flex items-center justify-between rounded-md border border-panel-border bg-card px-3 py-2">
            <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
              <GuideIcon className="h-4 w-4" />
              {platform ? getPlatformLabel(platform) : '…'}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[12px] text-muted-foreground">Offline Mode</span>
              <Switch
                checked={offlineEnabled}
                onCheckedChange={toggleOffline}
                data-testid="narr-offline-toggle"
              />
            </div>
          </div>

          {/* Readiness indicator */}
          {platform && (
            <ReadinessBadge
              isPWA={platform.isPWA}
              isIOS={platform.isIOS}
              swSupported={swSupported}
            />
          )}

          {/* Setup steps */}
          <div className="rounded-md border border-panel-border bg-card">
            <div className="flex items-center gap-1.5 border-b border-panel-border px-3 py-2">
              <GuideIcon className="h-3.5 w-3.5 text-primary" />
              <span className="text-[13.5px] font-semibold">{guide.title}</span>
            </div>
            <ol className="space-y-1.5 px-3 py-2.5">
              {guide.steps.map((step, i) => (
                <li key={i} className="flex items-start gap-2 text-[13px]">
                  <span className="flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary mt-0.5">{i + 1}</span>
                  <span className="text-foreground/90 leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
            {guide.note && (
              <p className="border-t border-panel-border px-3 py-2 text-[12.5px] text-muted-foreground italic">
                {guide.note}
              </p>
            )}
            {guide.warning && (
              <p className="border-t border-amber-200 bg-amber-50/30 px-3 py-2 text-[12.5px] text-amber-700 rounded-b-md">
                {guide.warning}
              </p>
            )}
          </div>

          {/* Cache status */}
          <div className="rounded-md border border-panel-border bg-card">
            <div className="flex items-center justify-between border-b border-panel-border px-3 py-2">
              <span className="text-[13.5px] font-semibold">Audio Cache</span>
              <Button
                size="sm" variant="ghost"
                className="h-6 gap-1 px-2 text-[12px] text-destructive hover:text-destructive"
                onClick={handleClearCache}
                disabled={cacheBusy || !cacheStats?.count}
                data-testid="narr-clear-cache-btn"
              >
                {cacheBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                Clear All
              </Button>
            </div>

            {cacheBusy ? (
              <div className="flex items-center gap-2 px-3 py-3 text-[13px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Scanning cache…
              </div>
            ) : !swSupported ? (
              <p className="px-3 py-2.5 text-[13px] text-muted-foreground">
                Service Worker not available in this browser. Use Chrome, Edge, or Safari 16.4+ for offline caching.
              </p>
            ) : cacheStats === null ? (
              <p className="px-3 py-2.5 text-[13px] text-muted-foreground">Could not read cache.</p>
            ) : cacheStats.count === 0 ? (
              <div className="flex items-center gap-2 px-3 py-2.5 text-[13px] text-muted-foreground">
                <Download className="h-3.5 w-3.5" />
                No chapters cached yet. Play a chapter to cache it.
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between px-3 py-1.5 text-[12.5px] text-muted-foreground border-b border-panel-border">
                  <span>{cacheStats.count} chapter audio file{cacheStats.count !== 1 ? 's' : ''}</span>
                  <span>{cacheStats.sizeMb} MB</span>
                </div>
                <ul className="max-h-32 overflow-y-auto scroll-thin divide-y divide-panel-border">
                  {cacheStats.chapters.map(ch => (
                    <li key={ch.url} className="flex items-center justify-between px-3 py-1 text-[12.5px]">
                      <span className="text-foreground/90">Chapter {ch.number}</span>
                      <span className="text-muted-foreground/70">{ch.sizeMb} MB</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* SW status */}
          <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
            {swSupported
              ? <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Service Worker supported</>
              : <><XCircle     className="h-3.5 w-3.5 text-destructive" /> Service Worker not supported in this browser</>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
